/**
 * @file: UnreadSync.tsx
 *
 * Drives the unread badge: an initial reconcile, a foreground poll, and a
 * resume reconcile on app activation. Mounted only for a live viewer; the
 * actual `/chat/unread` call is the session provider's debounced rxjs
 * pipeline — this only pings the trigger.
 */

import { FC, memo, useEffect } from 'react';
import { AppState } from 'react-native';

/** Background reconcile cadence while the app is foreground. */
const POLL_INTERVAL_MS = 60_000;

export const ChatUnreadSync: FC<{ refresh: () => void }> = memo(
  ({ refresh }) => {
    useEffect(() => {
      refresh();
      let timer: ReturnType<typeof setInterval> | null =
        AppState.currentState === 'active'
          ? setInterval(refresh, POLL_INTERVAL_MS)
          : null;
      const sub = AppState.addEventListener('change', state => {
        if (state === 'active') {
          refresh();
          if (timer == null) timer = setInterval(refresh, POLL_INTERVAL_MS);
        } else if (timer != null) {
          clearInterval(timer);
          timer = null;
        }
      });
      return () => {
        sub.remove();
        if (timer != null) clearInterval(timer);
      };
    }, [refresh]);
    return null;
  },
);
